'use client'

import { MarkdownRenderer } from '@client/components/renderers/MarkdownRenderer'
import { Badge } from '@client/components/ui/badge'
import { Button } from '@client/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@client/components/ui/card'
import { cn } from '@client/lib/utils'

import type { ArenaComment } from './types'

interface ArenaReviewPanelProps {
  side: 'A' | 'B'
  comment: ArenaComment
  revealed?: boolean
  revealedSource?: 'human' | 'ai'
  isChosen?: boolean
  onVote?: () => void
  disabled?: boolean
  className?: string
}

export function ArenaReviewPanel({
  side,
  comment,
  revealed = false,
  revealedSource,
  isChosen = false,
  onVote,
  disabled = false,
  className,
}: ArenaReviewPanelProps) {
  const canVote = onVote && !disabled

  return (
    <Card
      className={cn(
        'transition-all duration-300 relative',
        revealed && revealedSource === 'human' && 'border-blue-500 ring-2 ring-blue-500/20',
        revealed && revealedSource === 'ai' && 'border-orange-500 ring-2 ring-orange-500/20',
        isChosen && !revealed && 'ring-2 ring-primary/50',
        canVote && 'hover:border-primary hover:shadow-lg',
        className
      )}
    >
      <CardHeader className="px-3 py-2 sm:px-4 sm:py-3 md:px-6 md:py-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg font-semibold">Review {side}</CardTitle>
          {revealed && revealedSource ? (
            <Badge
              className={cn(
                revealedSource === 'human' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-orange-500 hover:bg-orange-600'
              )}
            >
              {revealedSource === 'human' ? 'Human' : 'AI'}
            </Badge>
          ) : (
            canVote && (
              <Button type="button" size="sm" variant="outline" className="text-xs" onClick={() => onVote?.()}>
                {side} is Human
              </Button>
            )
          )}
        </div>
        {revealed && isChosen && <p className="text-sm font-medium text-primary">Your guess</p>}
      </CardHeader>

      <CardContent className="p-3 sm:p-4">
        <MarkdownRenderer
          content={comment.content}
          className="prose prose-sm dark:prose-invert max-w-none text-foreground/90"
        />
      </CardContent>
    </Card>
  )
}
